import React, { useRef, useState, useEffect } from 'react';
import { useAuth } from '../../utils/AuthContext';
import UserAvatar from './UserAvatar';
import ProfilePhotoCropModal from './ProfilePhotoCropModal';
import { Loader2, Upload, Trash2 } from 'lucide-react';
import './ProfilePhotoSection.css';

const MAX_BYTES = 8 * 1024 * 1024;

/**
 * Upload / remove profile photo (all CRM roles). Uses AuthContext + Storage `users/{uid}/…`.
 * New uploads open a crop step (drag + zoom) before saving.
 */
const ProfilePhotoSection: React.FC = () => {
  const { user, updateProfilePhoto, clearProfilePhoto } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (cropSrc) URL.revokeObjectURL(cropSrc);
    };
  }, [cropSrc]);

  if (!user?.id) return null;

  const onPick = () => {
    setError(null);
    inputRef.current?.click();
  };

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(null);
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image (JPEG, PNG, or WebP).');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('Image must be 8 MB or smaller.');
      return;
    }
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(URL.createObjectURL(file));
  };

  const cancelCrop = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  };

  const saveCropped = async (file: File) => {
    setBusy(true);
    try {
      await updateProfilePhoto(file);
      cancelCrop();
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async () => {
    setError(null);
    setBusy(true);
    try {
      await clearProfilePhoto();
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Could not remove photo');
    } finally {
      setBusy(false);
    }
  };

  const hasPhoto = Boolean(user.photoURL && String(user.photoURL).trim());

  return (
    <div className="profile-photo-section">
      {cropSrc && (
        <ProfilePhotoCropModal imageSrc={cropSrc} onCancel={cancelCrop} onSave={saveCropped} />
      )}
      <div className="profile-photo-section-row">
        <UserAvatar
          name={user.name}
          photoURL={user.photoURL}
          userId={user.id}
          className="profile-photo-section-avatar"
          style={{ width: 64, height: 64, fontSize: 20 }}
        />
        <div className="profile-photo-section-actions">
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="profile-photo-section-input"
            onChange={onFile}
            aria-hidden
          />
          <button
            type="button"
            className="profile-photo-section-btn profile-photo-section-btn-primary"
            onClick={onPick}
            disabled={busy}
          >
            {busy ? <Loader2 size={16} className="profile-photo-section-spin" /> : <Upload size={16} />}
            {hasPhoto ? 'Change photo' : 'Upload photo'}
          </button>
          {hasPhoto && (
            <button type="button" className="profile-photo-section-btn" onClick={onRemove} disabled={busy}>
              <Trash2 size={16} />
              Remove
            </button>
          )}
        </div>
      </div>
      <p className="profile-photo-section-hint">
        Drag and zoom to frame your face in the circle, then save. Without a photo, your initials show everywhere. Max 8 MB.
      </p>
      {error && <p className="profile-photo-section-error">{error}</p>}
    </div>
  );
};

export default ProfilePhotoSection;
